import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { Stack } from '@mui/material';
import * as R from 'ramda';
import { makeStyles } from 'tss-react/mui';
import useWidth from '../../../../../components/Hooks/useWidth';
import HideableMoneyRenderer from '../../../../../components/Money/HiddeableMoneyRenderer/HideableMoneyRenderer';
import { MoneyRendererV3Props } from '../../../../../components/Money/MoneyRendererV3/moneyRendererV3.types';
import PriceCompositionMarker from '../../../../../components/Money/PriceCompositionMarker';
import Typography from '../../../../../components/v2/ui/Typography/Typography';
import { useOrderValueVisibility } from '../../../../../hooks/useOrderValueVisibility';
import LangEnum from '../../../../../lang/LangEnum';
import ReducedProductPricesV2 from '../../../../products/prices/components/ReducedProductPricesV2';
import { useTranslation } from '../../../../translation/TranslationContext';
import { PriceCompositionTotals, Totals } from '../../orderProducts.types';

type OrderResumeSize = 'small' | 'medium';

interface OrderResumeProps extends Totals {
  size?: OrderResumeSize;
  disableHideOrderValue?: boolean;
  priceCompositionParams?: {
    totals: PriceCompositionTotals[];
  };
}

const useStyles = makeStyles()((theme) => ({
  orderResumeContainer: {
    display: 'flex',
    wrap: 'nowrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'space-between',
    },
  },
}));

const moneyRendererSizeMap: Record<OrderResumeSize, MoneyRendererV3Props['size']> = {
  small: 'small',
  medium: 'medium',
};

const OrderResume = ({
  total,
  partsQuantity,
  itemsQuantity,
  size = 'medium',
  disableHideOrderValue = false,
  priceCompositionParams,
}: OrderResumeProps) => {
  const { mobileWidth } = useWidth();
  const { translate } = useTranslation();
  const { classes } = useStyles();

  const hasPriceCompositionParams = !!priceCompositionParams;
  const priceCompositionTotals = priceCompositionParams?.totals || [];
  const isPriceCompositionTooltipVisible = mobileWidth && priceCompositionTotals.length > 0;

  const { shouldHideOrderValues } = useOrderValueVisibility();

  const hasToHideOrderValues = !disableHideOrderValue && shouldHideOrderValues;

  return (
    <div className={classes.orderResumeContainer}>
      {!R.isNil(itemsQuantity) && (
        <Typography
          noWrap
          variant="body2"
          color={hasPriceCompositionParams ? 'textPrimary' : 'textSecondary'}
        >
          {translate(LangEnum.GLOBAL_ITEM, { quantity: itemsQuantity })}
        </Typography>
      )}
      <Typography
        noWrap
        variant="body2"
        color={hasPriceCompositionParams ? 'textPrimary' : 'textSecondary'}
      >
        {translate(LangEnum.GLOBAL_PIECE, { quantity: partsQuantity })}
      </Typography>
      <Tooltip
        TooltipContent={
          isPriceCompositionTooltipVisible && (
            <ReducedProductPricesV2
              priceCompositions={priceCompositionTotals.map((item) => ({
                label: item.label,
                price: item.totalValue,
              }))}
            />
          )
        }
      >
        <PriceCompositionMarker isActive={isPriceCompositionTooltipVisible}>
          <HideableMoneyRenderer
            value={total}
            hideValue={hasToHideOrderValues}
            size={moneyRendererSizeMap[size]}
            color={hasPriceCompositionParams ? 'textPrimary' : 'textSecondary'}
          />
        </PriceCompositionMarker>
      </Tooltip>
      {!mobileWidth &&
        priceCompositionTotals?.map((priceComposition, idx) => (
          <Stack
            direction="row"
            key={`${priceComposition.id}-${idx}`}
            sx={{
              gap: '4px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HideableMoneyRenderer
              color="textSecondary"
              hideValue={hasToHideOrderValues}
              size={moneyRendererSizeMap[size]}
              value={priceComposition.totalValue}
            />

            <Typography noWrap variant="body2" color="textSecondary">
              {priceComposition.label}
            </Typography>
          </Stack>
        ))}
    </div>
  );
};

export default OrderResume;
